import { EyeOff, Lightbulb } from 'lucide-react';
import { Panel, Progress, Skeleton } from '@/design/components';
import { useSurveyResults, type QuestionResult, type ResultSlice } from '../api';

/**
 * תוצאות הסקר, מסוכמות ומוסברות (מיגרציה 085).
 *
 * קודם זו הייתה רשימה של כל התשובות, שורה לכל אחת. בעשרים שאלות
 * ועשרות עובדים זה אלף שורות שאי אפשר להסיק מהן דבר.
 *
 * ליד כל שאלה מופיעה ההערה שכתב מי שבנה את הסקר: למה היא נשאלה
 * ומה נחשב תשובה טובה. בלי זה מי שקורא את התוצאות חצי שנה אחרי
 * כן רואה מספר ולא יודע אם 3.4 זה טוב או רע.
 */
export function SurveyResults({ surveyId }: { surveyId: string }) {
  const results = useSurveyResults(surveyId);

  if (results.isPending) return <Skeleton className="h-32" />;
  if (!results.data) return null;

  const data = results.data;
  const rate = data.total > 0 ? Math.round((100 * data.participated) / data.total) : 0;

  return (
    <Panel title={`תוצאות: ${data.title}`}>
      <div className="grid gap-4">
        <Progress
          value={data.participated}
          max={data.total}
          label={`${data.participated} מתוך ${data.total} ענו · ${rate}%`}
        />

        {/*
          מתחת ל-40% אי אפשר להסיק על הארגון, רק על מי שענה —
          ובדרך כלל זה מי שאכפת לו. עדיף שזה ייכתב כאן מאשר
          שמישהו יציג את המספרים בישיבה.
        */}
        {data.participated > 0 && rate < 40 && (
          <p className="m-0 rounded-sm border border-warn bg-raised p-2 text-body-sm">
            אחוז השתתפות נמוך. התוצאות מתארות את מי שענה, ולא בהכרח את כלל העובדים.
          </p>
        )}

        {data.is_anonymous && (
          <p className="m-0 flex items-center gap-2 text-body-sm text-muted">
            <EyeOff size={16} strokeWidth={1.75} aria-hidden="true" />
            סקר אנונימי. אין דרך לדעת מי ענה מה, גם לא מכאן.
          </p>
        )}

        {data.analysis_note && <Note>{data.analysis_note}</Note>}

        {data.participated === 0 ? (
          <p className="m-0 text-body-sm text-muted">עוד אין תשובות.</p>
        ) : (
          <div className="grid gap-3">
            {data.questions.map((question, index) => (
              <QuestionBlock
                key={question.id}
                question={question}
                index={index}
                showSection={question.section !== data.questions[index - 1]?.section}
              />
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function QuestionBlock({
  question,
  index,
  showSection,
}: {
  question: QuestionResult;
  index: number;
  showSection: boolean;
}) {
  return (
    <>
      {showSection && question.section && (
        <h3 className="m-0 mt-2 border-b border-line pb-1 text-title font-semibold text-ink">
          {question.section}
        </h3>
      )}

      <section className="grid gap-2 rounded-md border border-line p-3">
        <h4 className="m-0 text-body font-medium text-ink">
          <span className="text-muted">{index + 1}.</span> {question.prompt}
        </h4>

        <p className="m-0 flex flex-wrap gap-x-3 text-label text-muted">
          <span>{question.answered} ענו</span>
          {question.na > 0 && <span>{question.na} לא רלוונטי</span>}
          {question.skipped > 0 && <span>{question.skipped} דילגו</span>}
          {question.average !== null && (
            <span className="font-medium text-ink">ממוצע {question.average}</span>
          )}
          {question.answer_kind === 'multi' && <span>אפשר לסמן כמה, הסכום עובר 100%</span>}
        </p>

        {question.answer_kind === 'text' ? (
          <Texts texts={question.texts} />
        ) : (
          <Distribution slices={question.distribution} />
        )}

        {question.analysis_note && <Note>{question.analysis_note}</Note>}
      </section>
    </>
  );
}

/**
 * ההתפלגות כפסים.
 *
 * הפס הוא רוחב יחסי בלבד ואין לו משמעות של צבע סטטוס, ולכן הוא
 * אינו נושא צבע — המספר והאחוז כתובים לידו ממילא (כלל 10).
 */
function Distribution({ slices }: { slices: ResultSlice[] }) {
  if (slices.length === 0) return <p className="m-0 text-body-sm text-muted">אין תשובות.</p>;

  const top = Math.max(...slices.map((slice) => slice.count), 1);

  return (
    <ul className="m-0 grid list-none gap-1 p-0">
      {slices.map((slice) => (
        <li key={slice.label} className="grid grid-cols-[7rem_1fr_4.5rem] items-center gap-2">
          <span className="truncate text-body-sm text-ink" title={slice.label}>
            {slice.label}
          </span>
          <span className="h-2 rounded-pill bg-cream" aria-hidden="true">
            <span
              className="block h-full rounded-pill bg-olive"
              style={{ width: `${Math.round((100 * slice.count) / top)}%` }}
            />
          </span>
          <span className="tabular text-start text-body-sm text-muted">
            {slice.count} · {slice.percent}%
          </span>
        </li>
      ))}
    </ul>
  );
}

function Texts({ texts }: { texts: QuestionResult['texts'] }) {
  if (texts.length === 0) return <p className="m-0 text-body-sm text-muted">אין תשובות.</p>;

  return (
    <ul className="m-0 grid list-none gap-2 p-0">
      {texts.map((entry, index) => (
        <li key={index} className="rounded-sm border border-line bg-raised p-2">
          <p className="m-0 whitespace-pre-line text-body-sm text-ink">{entry.text}</p>
          {(entry.name || entry.branch) && (
            <p className="m-0 text-label text-muted">{entry.name ?? entry.branch}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** ההסבר שכתב מי שבנה את הסקר. נבדל מהנתונים, כי הוא פרשנות. */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 flex items-start gap-2 rounded-sm bg-cream p-2 text-body-sm text-ink">
      <Lightbulb size={16} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
      <span className="whitespace-pre-line">{children}</span>
    </p>
  );
}
